import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, Text } from '@chakra-ui/react';
import { ProgressInfo } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { COHORT_ID, MAX_INLINE_CHECK_IN_COMMENT_LENGTH } from 'Deer/intent/Goals/components/CheckInGoalsTable';
import { useIsCompanyGoalsManagePage } from 'Deer/intent/Goals/components/MetaFilters';
import { useCanEditProgressAndStatus } from 'Deer/intent/Goals/hooks/useCanEditProgressAndStatus';
import {
  setEditableRowData,
  updateEditableRowData,
  useSelectEditableRowData,
} from 'Deer/intent/Goals/slice/CheckInIntentSlice';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import CommentSection from 'components/RabbitInitiativeSidePanel/Components/CommentSection';
import CustomTagChipList from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/CustomTagChipList';
import {
  GoalStatusComponent,
  StatusTag,
  getSelectedOption,
} from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Overview/OKRStatus';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { formatDistanceToNow, startOfDay } from 'date-fns';
import { capitalize, get, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { generateKebabMenuOptionsRevamp, postProcessPopup } from 'screens/Team/Rabbit/Goals/AgGridTreeView/utils';
import { ObjectiveKRCell as ObjectiveKRCellResponsive } from 'screens/home/summary/components/Cards/Goals/components/ObjectiveKRCell';
import { selectOrgData } from 'selectors/orgData';
import { StyledTooltip } from 'styles/common';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { ChevronRight } from 'assets/assetsV1/iconComponents';
import { ArrowForward } from 'assets/assetsV1/iconComponents/ArrowForward';

import {
  CLOSED_STATUS_COLOR_MAP,
  GOAL_PROGRESS_VALUE_TO_OBJECT_MAP,
  INPROGRESS_STATUS_COLOR_MAP,
  MODULE_KEYS1,
  OBJECTIVE_LEVEL_TAGS,
  TASK_TYPES_2,
} from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import {
  convertDateToString4,
  getOrgGoalMeasurementTypes,
  notNullOrUndefined,
  roundOffToPlaces,
} from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import {
  sendAddedCommentBulkCheckInCompany,
  sendAddedCommentBulkCheckInDirectTeam,
  sendAddedCommentBulkCheckInMe,
  sendAddedCommentBulkCheckInSkipTeam,
  sendChangedAchievedValueBulkCheckInCompany,
  sendChangedAchievedValueBulkCheckInDirectTeam,
  sendChangedAchievedValueBulkCheckInMe,
  sendChangedAchievedValueBulkCheckInSkipTeam,
  sendChangedStatusBulkCheckInCompany,
  sendChangedStatusBulkCheckInDirectTeam,
  sendChangedStatusBulkCheckInMe,
  sendChangedStatusBulkCheckInSkipTeam,
  sendOpenedKRPanelEvent,
} from 'constants/mixpanelHelper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography';

import { EmptyBar } from '../Insights/RabbitGroupByIndividualInsightsTable';
import {
  CheckinChildrenCountComponent,
  CheckinCountComponent,
  ChildKrComponent,
  ChildrenObjectiveCountComponent,
  LastCheckInChildrenComponent,
  LastCheckInComponent,
} from '../Insights/RabbitGroupByObjectiveInsightsTable';
import { Collaborators } from './components/CommonTreeCell/Collabs';
import { ObjectiveKRCell } from './components/CommonTreeCell/ObjectiveKRCell';
import { ObjectiveKrWeightageCell } from './components/CommonTreeCell/ObjectiveKRWeightageCell';
import { Owners } from './components/CommonTreeCell/Owners';
import { OwnersAndCollaborators } from './components/CommonTreeCell/OwnersAndCollaborators';
import { StatusCell } from './components/CommonTreeCell/StatusCell';
import { CustomHeaderCell } from './components/CustomHeaderCell';
import { HeaderCellTooltip } from './components/HeaderCellTooltip';
import { LoadingOverlayComponent } from './components/LoadingOverlayComponent';
import { NoRowsOverlayComponent } from './components/NoRowsOverlayComponent';
import { useOKRManageViewType } from './hooks/useOKRManageViewType';
import './styles/Menu.css';
import './styles/TreeView.css';

const columnsToHide = [
  'table-last-checkin-children',
  'table-last-checkin',
  'table-checkin-children-count',
  'table-checkin-count',
  'table-child-kr-count',
  'table-child-objective-count',
];

const sendChangedStatusBulkCheckInEvent = (selectedCohortId) => {
  switch (selectedCohortId) {
    case COHORT_ID.ME:
      sendChangedStatusBulkCheckInMe();
      break;
    case COHORT_ID.DIRECT_TEAM:
      sendChangedStatusBulkCheckInDirectTeam();
      break;
    case COHORT_ID.SKIP_TEAM:
      sendChangedStatusBulkCheckInSkipTeam();
      break;
    case COHORT_ID.COMPANY:
      sendChangedStatusBulkCheckInCompany();
      break;
    default:
      break;
  }
};

const sendChangedAchievedValueBulkCheckInEvent = (selectedCohortId) => {
  switch (selectedCohortId) {
    case COHORT_ID.ME:
      sendChangedAchievedValueBulkCheckInMe();
      break;
    case COHORT_ID.DIRECT_TEAM:
      sendChangedAchievedValueBulkCheckInDirectTeam();
      break;
    case COHORT_ID.SKIP_TEAM:
      sendChangedAchievedValueBulkCheckInSkipTeam();
      break;
    case COHORT_ID.COMPANY:
      sendChangedAchievedValueBulkCheckInCompany();
      break;
    default:
      break;
  }
};

const sendAddedCommentBulkCheckInEvent = (selectedCohortId) => {
  switch (selectedCohortId) {
    case COHORT_ID.ME:
      sendAddedCommentBulkCheckInMe();
      break;
    case COHORT_ID.DIRECT_TEAM:
      sendAddedCommentBulkCheckInDirectTeam();
      break;
    case COHORT_ID.SKIP_TEAM:
      sendAddedCommentBulkCheckInSkipTeam();
      break;
    case COHORT_ID.COMPANY:
      sendAddedCommentBulkCheckInCompany();
      break;
    default:
      break;
  }
};

function Companies({ data }) {
  const { companies = [] } = data;
  return (
    <Box>
      {companies.map(({ companyName }, index) => (
        <TextBodyMedium key={companyName} as="span">
          {companyName}
          {index !== companies.length - 1 && ', '}
        </TextBodyMedium>
      ))}
    </Box>
  );
}

function EmpLocations({ data }) {
  const { empLocations = [] } = data;
  return (
    <Box>
      {empLocations.map((empLocation, index) => (
        <TextBodyMedium key={empLocation} as="span">
          {empLocation}
          {index !== empLocations.length - 1 && ', '}
        </TextBodyMedium>
      ))}
    </Box>
  );
}

function ProgressInfoComponent({ data, isCheckIn = false }) {
  const ERROR_MSG = 'This is a mandatory field';
  const [error, setError] = useState('');
  const hasError = error.length > 0;
  const sideNavSelectedCohort = useSelectedView();

  const { uuid, startingProgressValue, target, metric, achieved, unMasked } = data ?? {};
  const orgData = useSelector(selectOrgData);
  const kRMeasurementTypes = getOrgGoalMeasurementTypes(orgData?.currency ?? null);
  const dispatch = useDispatch();
  const canEditProgress = useCanEditProgressAndStatus(data);
  const metricName = data?.metricName || 'Progress';
  const shortMetricName = metricName.length > 20 ? `${metricName.slice(0, 20)}...` : metricName;

  const selectedMeasure = kRMeasurementTypes.find(({ code }) => code === metric);

  useEffect(() => {
    // on cancel we need to reset the error
    if (error && achieved !== '') {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achieved]);

  if (isCheckIn) {
    return (
      <Box width="100%" height="100%">
        <Box display="flex" flexDir="column" alignItems="flex-start" padding="14px 14px 8px 14px" gap="14px">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            gap="4px"
            width="100%"
          >
            <Box display="flex" padding="2px 0px" alignItems="center" gap="4px" width="100%">
              <TextLabelSmall color="#666" fontSize="0.5rem" lineHeight="0.75rem">
                {shortMetricName || 'Progress'}
                <Text color="#C62C02" as="span">
                  *
                </Text>
              </TextLabelSmall>
            </Box>
            <Box display="flex" alignItems="center" gap="4px" width="100%">
              <Box
                display="flex"
                padding="0px 4px"
                alignItems="center"
                gap="10px"
                flex="1 0 0"
                borderRadius="4px 4px 0px 0px"
              >
                {canEditProgress ? (
                  <Box>
                    <InputV2
                      value={achieved}
                      isError={hasError}
                      onChange={(ev) => {
                        const { value } = ev.target;
                        const isEmpty = value?.trim()?.length === 0;
                        setError(isEmpty ? ERROR_MSG : '');
                        dispatch(
                          updateEditableRowData({
                            uuid,
                            updatedData: { achieved: value, hasError: isEmpty },
                          })
                        );
                      }}
                      onBlur={() => {
                        if (hasError) return;
                        sendChangedAchievedValueBulkCheckInEvent(sideNavSelectedCohort?.id);
                        dispatch(
                          updateEditableRowData({
                            uuid,
                            updatedData: { achieved: roundOffToPlaces(achieved, 2) },
                          })
                        );
                      }}
                      onWheel={(event) => {
                        // to disable value change on scroll inside the input
                        event.currentTarget.blur();
                      }}
                      type="number"
                      height="20px"
                      padding="0px"
                      color="#333"
                      fontSize="14px"
                      fontStyle="normal"
                      fontWeight="600"
                      lineHeight="140%"
                    />
                  </Box>
                ) : (
                  <TextBodyMedium color="#333" fontSize="14px" fontStyle="normal" fontWeight="600" lineHeight="140%">
                    {roundOffToPlaces(achieved, 2)}
                  </TextBodyMedium>
                )}
              </Box>
              <Box display="flex" padding="0px 4px" alignItems="center">
                <TextBodyMedium bold>{selectedMeasure?.symbol}</TextBodyMedium>
              </Box>
            </Box>
          </Box>
          <Box display="flex" alignItems="flex-start" gap="4px" width="100%" justifyContent="space-between">
            <TextLabelSmall color="#666">
              {roundOffToPlaces(startingProgressValue, 2)}
              {selectedMeasure?.symbol}
            </TextLabelSmall>
            <ArrowForward color="#666" />
            <TextLabelSmall color="#666">
              {roundOffToPlaces(target, 2)}
              {selectedMeasure?.symbol}
            </TextLabelSmall>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!unMasked) {
    return null;
  }

  return (
    <ProgressInfo
      status={data?.closureStatus ? `${data?.progressLevel}_${data?.closureStatus}` : data?.progressLevel}
      startValue={Number(data?.startingProgressValue || 0)}
      targetValue={Number(data?.target || 100)}
      achievedValue={Number(data?.achieved || 0)}
      measureBy={{ ...selectedMeasure, isPrefix: false }}
      metricName={data?.metricName || 'Progress'}
      percentageToDisplay={data?.avgProgressPercentage ?? 0}
    />
  );
}

function CommentComponent({ data, refetchHandler }) {
  const { uuid, newComment = '', commentPlainText, lastCheckIn, type, commentState = null } = data;
  const [isCommentInputInFocus, setIsCommentInputInFocus] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const sideNavSelectedCohort = useSelectedView();

  return (
    <Box width="100%" height="100%">
      <Box
        display="flex"
        width="100%"
        padding="0px 12px"
        flexDirection="column"
        justifyContent="flex-end"
        alignItems="flex-start"
        gap="4px"
        alignSelf="stretch"
        marginTop="9px"
      >
        <StyledTooltip title={commentPlainText ?? ''} placement="left">
          <Box
            display="flex"
            padding="10px 12px 2px 0px"
            alignItems="flex-end"
            flex="1 0 0"
            alignSelf="stretch"
            sx={{ '.checkIn-comment': { height: '42px!important' } }}
          >
            <Box
              width="100%"
              className="checkIn-comment-container"
              sx={{
                '& > div > div': {
                  background: 'transparent',
                },
                '& > div > div > div': {
                  border: 'none',
                  borderRadius: '0px',
                  borderBottom: `1px ${
                    newComment?.length > MAX_INLINE_CHECK_IN_COMMENT_LENGTH ? 'solid #c62c02' : `dashed #999`
                  }`,
                  '&:hover': {
                    borderBottomStyle: 'solid',
                  },
                },
                '& .public-DraftStyleDefault-block.public-DraftStyleDefault-ltr': {
                  lineHeight: '21px',
                },
                '& .public-DraftEditorPlaceholder-inner': {
                  lineHeight: '21px',
                },
                '.editorContainer': {
                  minHeight: '21px',
                  padding: '0px',
                },
                '.DraftEditor-editorContainer': {
                  maxHeight: isCommentInputInFocus ? '42px !important' : '21px !important',
                },
              }}
            >
              <CommentSection
                key={uuid}
                taskId={uuid}
                taskType={TASK_TYPES_2.task}
                allowEnterToNextLine
                isEnterCommentAlwaysActive
                parentSource="CheckIn Panel"
                hideAvatar
                contentState={commentState || null}
                isCommentAllowedWithEnterButton={false}
                handleBlur={() => {
                  sendAddedCommentBulkCheckInEvent(sideNavSelectedCohort?.id);
                  setIsCommentInputInFocus(false);
                }}
                handleFocus={() => {
                  setIsCommentInputInFocus(true);
                }}
                setModifiedCommentState={(
                  newContentState,
                  media,
                  visibility,
                  sentiment,
                  isCommentExist,
                  commentPayloadType
                ) => {
                  const replaceEmployeeTags = (inputString, replacement) => {
                    // regex to select all the text in the format @{{pathToEmployeeName}}
                    const regex = /@\{\{[^}]*\}\}/g;
                    return inputString.replace(regex, replacement);
                  };

                  const originalText = commentPayloadType?.text || '';
                  const replacedText = replaceEmployeeTags(originalText, (match) => {
                    const objectPath = match.slice(3, match.length - 2);

                    const replacement = get(commentPayloadType?.macros || {}, objectPath, '');

                    return `@${replacement}`;
                  });
                  // setTimeout is necessary to avoid aggrid re-rendering custom cell renderer while the last row rendering is in progress
                  setTimeout(() => {
                    dispatch(
                      updateEditableRowData({
                        uuid,
                        updatedData: {
                          newComment: commentPayloadType?.text || '',
                          commentPayloadType,
                          commentState: newContentState,
                          commentPlainText: replacedText,
                        },
                        hasError: commentPayloadType?.text?.length > MAX_INLINE_CHECK_IN_COMMENT_LENGTH,
                      })
                    );
                  }, 0);
                }}
                enableKudos={false}
                useEmojiFunctionality={false}
                placeholderText="Add comments"
              />
            </Box>
          </Box>
        </StyledTooltip>
        {/*  eslint-disable-next-line react/no-unstable-nested-components */}

        <Button
          variant={BUTTON_VARIANT.tertiary}
          padding="0px!important"
          sx={{ p: { margin: '0px', fontSize: '0.625rem', lineHeight: '1rem', fontWeight: 400 } }}
          EndIcon={ChevronRight}
          endIconProps={{ boxSize: '16px' }}
          onClick={() => {
            dispatch(
              showOKRDetails({
                id: uuid,
                type,
                onDelete: refetchHandler,
                onUpdate: refetchHandler,
                scrollToId: 'History',
                history,
              })
            );
          }}
          height="28px"
        >
          Last check-in:{' '}
          {lastCheckIn
            ? formatDistanceToNow(lastCheckIn, {
                addSuffix: true,
              })
            : 'Never'}
        </Button>
      </Box>
    </Box>
  );
}

function CheckInStatusComponent({ data: rowData }) {
  const { progressLevel, closureStatus, uuid } = rowData ?? {};
  const canEditStatus = useCanEditProgressAndStatus(rowData);
  const selectedOption = getSelectedOption({ goalClosureStatus: closureStatus, goalProgressLevel: progressLevel });
  const dispatch = useDispatch();
  const sideNavSelectedCohort = useSelectedView();

  const selectedOptionColors =
    selectedOption?.progressLevel === 'CLOSED'
      ? CLOSED_STATUS_COLOR_MAP[selectedOption?.value]
      : INPROGRESS_STATUS_COLOR_MAP[selectedOption?.value];

  return (
    <Flex width="100%" height="100%" justifyContent="flex-start" alignItems="baseline">
      {canEditStatus ? (
        <Box marginBottom="26px">
          <GoalStatusComponent
            goalClosureStatus={closureStatus}
            goalProgressLevel={progressLevel}
            isShortLabel
            isPortalRequired
            onChange={({ goalClosureStatus, goalProgressLevel }) => {
              sendChangedStatusBulkCheckInEvent(sideNavSelectedCohort?.id);
              dispatch(
                updateEditableRowData({
                  uuid,
                  updatedData: { progressLevel: goalProgressLevel, closureStatus: goalClosureStatus },
                })
              );
            }}
          />
        </Box>
      ) : (
        <StatusTag bg={selectedOptionColors?.tagColor}>
          <TextBodyMedium color={selectedOptionColors?.textColor}>{selectedOption?.label}</TextBodyMedium>
        </StatusTag>
      )}
    </Flex>
  );
}

const GoalsObjectiveGenericTreeViewTable = ({
  gridRef,
  data,
  refetchHandler,
  showReviewWeightageColumn = false,
  hasImplicitDepartmentFilter = false,
  /* filterModelProps */
  gridStatePersistenceKey = null,
  setChartData = () => {},
  onModalUpdated = () => {},
  isReview = false,
  source = '',
  isCheckIn = false,
}) => {
  const location = useLocation();
  const history = useHistory();
  const page = useViewPageName();
  const dispatch = useDispatch();

  const { viewType } = useOKRManageViewType();

  const { module } = useSidebarNavMatchRoute();

  const { type, id: selectedCohortId } = useSelectedView() ?? {};

  const isMyOkr = type === 'MY_OKRs';
  const isMyCompany = type === 'ALL_EMPLOYEE';

  const [rowData, setRowData] = useState(data);
  const editableRowData = useSelectEditableRowData();
  // using useState to save grid row data because without useState loading overlay of ag grid doesn't work. Ag grid is not able to correctly detect when we are loading data and when is our data empty
  useEffect(() => {
    dispatch(
      setEditableRowData({
        rowData: data ? data.map((row) => ({ ...row, achieved: roundOffToPlaces(row?.achieved, 2) })) : [],
      })
    );
    setRowData(data);
  }, [data, dispatch]);

  const handleDataModelUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    setChartData((prev) => ({ ...prev, rowData: newRowData }));
  }, [gridRef, setChartData]);

  const { objectivesName, moduleName, keyResultsName } = useOrganisationSettings();

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const getProgressTooltip = () =>
    `${objectivesName} progress includes ${moduleName} that may not be visible in the filtered list. This is to ensure everyone has right view on the ${objectivesName}'s current progress.`;

  const getStatusTooltip = () =>
    `Hover on the chart to visualise the status distribution of all the child ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} for the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getChildObjectiveTooltip = () =>
    `Shows the number of child ${modifyString(
      objectivesName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} aligned to the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getChildKRTooltip = () =>
    `Shows the number of child ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} aligned to the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getSelfCheckInCountTooltip = () =>
    `Shows the number of direct check-ins made on the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )}. Only direct changes made to the status or progress of the ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} would be regarded as check-ins. We would not be accounting for any changes made to child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in this column.`;

  const getCheckInCountTooltip = () =>
    `Shows the number of check-ins made on the entire ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} tree with the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} at the parent level. Any change in the status or progress of child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} would also be recorded as a check-in and the value would be reflected in this column.`;

  const getLastCheckInTooltip = () =>
    `Shows the date of the last check-in made on the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )}.Only direct changes made to the status or progress of the ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} would be regarded as check-ins. We would not be accounting for any changes made to child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in this column.`;

  const getLastChildCheckInTooltip = () =>
    `Shows the date of the last check-in made on the entire ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} tree with the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} at the parent level. Any change in the status or progress of child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} would also be recorded as a check-in and the same would be reflected in this column.`;

  const isCompanyGoalsManagePage = useIsCompanyGoalsManagePage();

  const columnDefs = [
    // we're using the auto group column by default!
    /*
        IMPORTANT: whenever adding a new column or removing a column
        please update CSV download function on src/screens/Team/Rabbit/Goals/Insights/index.js
        Right now it does some calculations based on the column index
        TODO: make it more robust and remove the dependency on column index
      */
    {
      colId: 'type',
      field: 'type',
      headerName: 'Type',
      order: 21,
      minWidth: 150,
      maxWidth: 150,
      hide: true,
      valueGetter: (data) => data.data?.type,
      suppressColumnsToolPanel: true,
    },
    {
      colId: 'target',
      field: 'target',
      order: 22,
      headerName: 'Target',
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      colId: 'measure',
      field: 'metric',
      order: 23,
      headerName: 'Measure',
      hide: true,
      suppressColumnsToolPanel: true,
    },

    {
      field: 'toIds',
      filter: 'agMultiColumnFilter',
      colId: 'table-owner',
      sortable: true,
      order: 24,
      headerName: 'Owner',
      minWidth: isCheckIn ? 136 : 150,
      maxWidth: 150,
      lockPosition: isCheckIn ? 'left' : null,
      lockVisible: isCheckIn,
      order: 2,
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Function',
            filterParams: {
              valueFormatter: ({ value }) => {
                if (value) return value;
              },
              getValue: (data) =>
                data.data?.toIds
                  ?.map(({ employeeFunction, ownerType }) => ownerType === 'OWNER' && employeeFunction)
                  ?.filter((v) => v),
              cellRenderer: (params) => (
                <StyledTooltip title={params?.value ?? ''}>
                  <p>{params?.value ?? '(Blanks)'}</p>
                </StyledTooltip>
              ),
            },
          },
          {
            filter: 'agSetColumnFilter',
            title: 'Filter by Name',
            display: 'accordion',
            filterParams: {
              applyMiniFilterWhileTyping: true,

              getValue: (data) =>
                data.data?.toIds?.map(({ displayName, ownerType }) => (ownerType === 'OWNER' ? displayName : '')),
              cellRenderer: (params) => (
                <StyledTooltip title={params?.value ?? ''}>
                  <p>{params?.value ?? '(Blanks)'}</p>
                </StyledTooltip>
              ),
            },
          },
        ],
      },
      valueGetter: (data) =>
        data.data?.toIds?.map(({ ownerType, displayName }) => ownerType === 'OWNER' && displayName),
      cellRenderer: Owners,
    },
    ...(isCheckIn
      ? [
          {
            colId: 'checkinStatus',
            field: 'progressLevel',
            headerName: 'Status',
            // suppressColumnsToolPanel: true,
            valueGetter: (params) => (params?.data?.progressLevel || '') + (params?.data?.closureStatus || ''),
            cellRenderer: CheckInStatusComponent,
            lockPosition: isCheckIn ? 'left' : null,
            lockVisible: isCheckIn,
            minWidth: 150,
            maxWidth: 150,
            order: 3,
            filter: 'agMultiColumnFilter',
            cellStyle: { overflowY: 'visible', overflowX: 'visible', padding: '0px', paddingLeft: '18px' },
            filterParams: {
              filters: [
                {
                  filter: 'agSetColumnFilter',
                  display: 'accordion',
                  title: 'Filter by status',
                  filterParams: {
                    applyMiniFilterWhileTyping: true,
                    values: ['ON_TRACK', 'OFF_TRACK', 'AT_RISK', 'CLOSED'],
                    valueFormatter: ({ value }) =>
                      GOAL_PROGRESS_VALUE_TO_OBJECT_MAP[value]?.label ?? capitalize(startCase(value)),
                    getValue: (data) => {
                      //  if you want filter to use includes in array, just return an array
                      const { progressLevel } = data?.data || {};

                      if (isCheckIn && progressLevel) {
                        return [progressLevel];
                      }
                      return Object.keys(data?.data?.progress?.progressCount ?? {});
                    },
                  },
                },
              ],
            },
          },
        ]
      : []),
    {
      field: 'toIds',
      filter: 'agMultiColumnFilter',
      sortable: true,
      headerName: 'Collaborators',
      colId: 'table-collaborators',
      hide: true,
      minWidth: 150,
      // maxWidth: 200,
      order: 6,
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Function',
            filterParams: {
              valueFormatter: ({ value }) => {
                if (value) return value;
              },
              getValue: (data) =>
                data.data?.toIds
                  ?.map(({ employeeFunction, ownerType }) => ownerType === 'COLLABORATOR' && employeeFunction)
                  ?.filter((v) => v),
              cellRenderer: (params) => (
                <StyledTooltip title={params?.value ?? ''}>
                  <p>{params?.value ?? '(Blanks)'}</p>
                </StyledTooltip>
              ),
            },
          },
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Name',
            filterParams: {
              applyMiniFilterWhileTyping: true,
              valueFormatter: ({ value }) => {
                if (value) return value;
                return '(Blanks)';
              },
            },
          },
        ],
      },
      valueGetter: (data) =>
        data.data?.toIds?.map(({ displayName, ownerType }) => ownerType === 'COLLABORATOR' && displayName),
      cellRenderer: Collaborators,
    },
    {
      field: 'toIds',
      filter: 'agMultiColumnFilter',
      colId: 'table-team',
      hide: true,
      sortable: true,
      headerName: 'Team',
      minWidth: 150,
      order: 7,
      // maxWidth: 200,
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Function',
            filterParams: {
              valueFormatter: ({ value }) => {
                if (value) return value;
              },
              getValue: (data) =>
                data.data?.toIds?.map(({ employeeFunction, ownerType }) => employeeFunction)?.filter((v) => v),
              cellRenderer: (params) => (
                <StyledTooltip title={params?.value ?? ''}>
                  <p>{params?.value ?? '(Blanks)'}</p>
                </StyledTooltip>
              ),
            },
          },
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Name',
            filterParams: {
              applyMiniFilterWhileTyping: true,
              valueFormatter: ({ value }) => {
                if (value) return value;
                return '(Blanks)';
              },
            },
          },
        ],
      },
      valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
      cellRenderer: OwnersAndCollaborators,
    },
    ...(showReviewWeightageColumn
      ? [
          {
            // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
            // if you've other data in cell renderer
            colId: 'weightage',
            field: 'weightage',
            headerName: 'Weightage',
            minWidth: 120,
            maxWidth: 250,
            filter: true,
            sortable: true,

            cellRenderer: ObjectiveKrWeightageCell,
          },
        ]
      : []),
    {
      // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
      // if you've other data in cell renderer
      field: 'startDate',
      headerName: 'Start Date',
      colId: 'table-start-date',
      hide: true,
      minWidth: 130,
      maxWidth: 130,
      sortable: true,
      order: 8,
      filter: 'agDateColumnFilter',
      filterValueGetter: (params) => (params?.data?.startDate ? startOfDay(new Date(params?.data?.startDate)) : null),
      valueGetter: (params) => (params?.data?.startDate ? params?.data?.startDate : null),
      cellRenderer: ({ data }) => {
        const { startDate } = data ?? {};

        return (
          <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
            {notNullOrUndefined(startDate) ? (
              <TextBodyMedium>{convertDateToString4(startDate)}</TextBodyMedium>
            ) : (
              <EmptyBar />
            )}
          </Flex>
        );
      },
    },
    {
      // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
      // if you've other data in cell renderer
      field: 'dueDate',
      headerName: 'Due Date',
      colId: 'table-due-date',
      hide: true,
      minWidth: 130,
      maxWidth: 130,
      sortable: true,
      order: 9,
      filter: 'agDateColumnFilter',
      filterValueGetter: (params) => (params?.data?.dueDate ? startOfDay(new Date(params?.data?.dueDate)) : null),
      valueGetter: (params) => (params?.data?.dueDate ? params?.data?.dueDate : null),
      cellRenderer: ({ data }) => {
        const { dueDate } = data ?? {};

        return (
          <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
            {notNullOrUndefined(dueDate) ? (
              <TextBodyMedium>{convertDateToString4(dueDate)}</TextBodyMedium>
            ) : (
              <EmptyBar />
            )}
          </Flex>
        );
      },
    },
    {
      // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
      // if you've other data in cell renderer
      field: 'progress',
      headerName: 'Progress',
      headerTooltip: getProgressTooltip(),
      tooltipComponent: HeaderCellTooltip,
      colId: 'table-status',
      minWidth: isCheckIn ? 120 : 340,
      maxWidth: isCheckIn ? 120 : 360,
      sortable: false,
      filter: 'agMultiColumnFilter',
      order: 4,
      filterParams: {
        filters: isCheckIn
          ? [
              {
                display: 'accordion',
                title: `Filter by progress`,
                filter: 'agNumberColumnFilter',
                filterParams: {
                  filterOptions: ['equals', 'inRange'],
                  getValue: (params) => Number(params?.data?.achieved?.toFixed(2) || 0),
                  debounceMs: 200,
                  suppressAndOrCondition: true,
                  inRangeInclusive: true,
                },
              },
            ]
          : [
              {
                filter: 'agSetColumnFilter',
                display: 'accordion',
                title: 'Filter by Status',
                filterParams: {
                  applyMiniFilterWhileTyping: true,
                  values: ['ON_TRACK', 'OFF_TRACK', 'AT_RISK', 'CLOSED'],
                  valueFormatter: ({ value }) =>
                    GOAL_PROGRESS_VALUE_TO_OBJECT_MAP[value]?.label ?? capitalize(startCase(value)),
                  getValue: (data) =>
                    //  if you want filter to use includes in array, just return an array
                    Object.keys(data?.data?.progress?.progressCount ?? {}),
                },
              },
              {
                display: 'accordion',
                title: `Filter by progress`,
                filter: 'agNumberColumnFilter',
                filterParams: {
                  filterOptions: ['equals', 'inRange'],
                  getValue: (params) => Number(params?.data?.avgProgressPercentage?.toFixed(2) || 0),
                  debounceMs: 200,
                  suppressAndOrCondition: true,
                  inRangeInclusive: true,
                },
              },
            ],
      },
      valueGetter: (params) =>
        params?.data?.progressLevel +
        params?.data?.avgProgressPercentage +
        params?.data?.closureStatus +
        params?.data?.startingProgressValue +
        params?.data?.target +
        params?.data?.achieved +
        params?.data?.metric +
        params?.data?.metricName,
      cellRenderer: ProgressInfoComponent,
      lockVisible: isCheckIn,
      floatingFilterComponentParams: { suppressFilterButton: !!isCheckIn },
      autoHeight: true,
      cellStyle: { height: '100%', padding: 0 },
      cellRendererParams: {
        isCheckIn,
      },
      lockPosition: isCheckIn ? 'left' : null,
    },
    ...(isCheckIn
      ? [
          {
            field: 'comment',
            headerName: 'Comments',
            colId: 'comment',
            minWidth: 200,
            maxWidth: 360,
            sortable: true,
            filter: 'agTextColumnFilter',
            filterParams: {
              getValue: (params) => params?.data?.commentPlainText || '',
            },
            headerComponent: CustomHeaderCell,
            headerComponentParams: {
              enableMenu: true,
              enableSorting: true,
            },
            valueGetter: (params) => (params?.data?.newComment || '') + (params?.data?.lastCheckIn || ''),
            cellRenderer: 'CommentComponent',
            lockVisible: isCheckIn,
            cellRendererParams: { refetchHandler },
            order: 5,
            autoHeight: true,
            cellStyle: { height: 'auto', padding: 0 },
            suppressFiltersToolPanel: true,
            lockPosition: isCheckIn ? 'left' : null,
          },
        ]
      : []),

    ...[
      {
        colId: 'status',
        field: 'progress',
        headerName: `Child ${keyResultsName} Status`,
        headerTooltip: getStatusTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        minWidth: 100,
        maxWidth: 100,
        filter: 'agMultiColumnFilter',
        sortable: false,
        order: 10,
        headerClass: 'truncate-header-ag-grid',
        filterParams: {
          filters: [
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'On-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.ON_TRACK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Off-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.OFF_TRACK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'At-Risk count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.AT_RISK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Closed count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.CLOSED?.count ?? 0,
              },
            },
          ],
        },
        valueGetter: (params) => params?.progress?.progressCount ?? {},
        cellRenderer: ({ data }) => {
          const { ifGoalChildExists, progress } = data ?? {};

          const { overallCount } = progress ?? {};

          if (!ifGoalChildExists || !overallCount) {
            const tooltipText = !overallCount
              ? `${moduleName}(s) marked as dependent in the child ${modifyString(
                  moduleName,
                  StringModifier.SENTENCE_CASE
                )} tree has no child ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`
              : `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} has no child ${modifyString(
                  keyResultsName,
                  StringModifier.PLURAL_SENTENCE_CASE
                )}`;
            return (
              <Tooltip label={tooltipText}>
                <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
                  <TextBodyMedium>NA</TextBodyMedium>
                </Flex>
              </Tooltip>
            );
          }

          return <StatusCell progress={progress} />;
        },
      },
      {
        field: 'customTags',
        headerName: 'Tags',
        colId: 'table-tags',
        minWidth: 300,
        order: 11,
        filter: 'agSetColumnFilter',
        sortable: false,
        filterParams: {
          applyMiniFilterWhileTyping: true,
          getValue: (params) => params?.data?.tagKeyList?.map((tagObj) => tagObj.tagName) || [],
        },
        valueGetter: (params) => params?.data?.tagKeyList?.map((tagObj) => tagObj.tagName) || [],
        cellRenderer: (params) =>
          params?.data?.tagKeyList?.length > 0 ? (
            <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
              <CustomTagChipList tags={params?.data?.tagKeyList || []} tagsLimit={3} />
            </Flex>
          ) : (
            <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
              <TextBodyMedium padding="10px">None</TextBodyMedium>{' '}
            </Flex>
          ),

        // to hide the column completely in case of usage in reviews module
        // hide: module === MODULE_KEYS1.REVIEW ? true : false,
        hide: true,
        suppressColumnsToolPanel: module === MODULE_KEYS1.REVIEW,
      },
    ],
    {
      field: 'startingProgressValue',
      headerName: 'Starting',
      order: 12,
      hide: true,
      suppressColumnsToolPanel: true,
      colId: 'table-starting-progress',
    },
    {
      field: 'achieved',
      headerName: 'Achieved',
      order: 13,
      hide: true,
      suppressColumnsToolPanel: true,
      colId: 'table-achieved-progress',
    },
    {
      field: 'childObjectiveCount',
      headerName: `No. of Child ${modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)}`,
      headerTooltip: getChildObjectiveTooltip(),
      tooltipComponent: HeaderCellTooltip,
      tooltipComponentParams: { showColumnName: true },
      order: 14,
      width: 120,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      hide: true,
      filter: 'agNumberColumnFilter',
      type: 'numericColumn',
      filterValueGetter: (params) => params?.data?.childObjectiveCount ?? 0,
      valueGetter: (params) => params.data?.childObjectiveCount ?? 0,
      cellRenderer: ChildrenObjectiveCountComponent,
      colId: 'table-child-objective-count',
      sortable: true,
    },
    {
      field: 'childKrCount',
      filter: 'agNumberColumnFilter',
      headerName: `No. of child ${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}`,
      headerTooltip: getChildKRTooltip(),
      tooltipComponent: HeaderCellTooltip,
      order: 15,
      tooltipComponentParams: { showColumnName: true },
      width: 120,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      type: 'numericColumn',
      filterValueGetter: (params) => params?.data?.childKrCount ?? 0,
      valueGetter: (params) => params.data?.childKrCount ?? 0,
      cellRenderer: ChildKrComponent,
      colId: 'table-child-kr-count',
      sortable: true,
    },
    {
      field: 'checkinCount',
      headerName: `No. of Check-ins on the ${moduleName}`,
      headerTooltip: getSelfCheckInCountTooltip(),
      tooltipComponent: HeaderCellTooltip,
      tooltipComponentParams: { showColumnName: true },
      order: 16,
      width: 120,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      filter: 'agNumberColumnFilter',
      type: 'numericColumn',
      filterValueGetter: (params) => params?.data?.selfCheckInCount ?? 0,
      valueGetter: (params) => params?.data?.selfCheckInCount ?? 0,
      cellRenderer: CheckinCountComponent,
      colId: 'table-checkin-count',
      sortable: true,
    },
    {
      field: 'checkinCount',
      headerName: `No. of Check-ins on the ${moduleName} Tree`,
      headerTooltip: getCheckInCountTooltip(),
      tooltipComponent: HeaderCellTooltip,
      order: 17,
      hide: true,
      tooltipComponentParams: { showColumnName: true },
      width: 120,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      filter: 'agNumberColumnFilter',
      type: 'numericColumn',
      filterValueGetter: (params) => params?.data?.checkInCount ?? 0,
      valueGetter: (params) => params?.data?.checkInCount ?? 0,
      cellRenderer: CheckinChildrenCountComponent,
      colId: 'table-checkin-children-count',
      sortable: true,
    },
    {
      field: 'lastCheckin',
      headerName: 'Last Check-In',
      order: 18,
      headerTooltip: getLastCheckInTooltip(),
      tooltipComponent: HeaderCellTooltip,
      tooltipComponentParams: { showColumnName: true },
      width: 140,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      filter: 'agDateColumnFilter',
      filterValueGetter: (params) =>
        params?.data?.lastCheckIn ? startOfDay(new Date(params?.data?.lastCheckIn)) : null,

      valueGetter: (params) => (params?.data?.lastCheckIn ? convertDateToString4(params?.data?.lastCheckIn) : ''),
      cellRenderer: LastCheckInComponent,
      colId: 'table-last-checkin',
      sortable: true,
    },
    {
      field: 'lastCheckin',
      headerName: `Last Check-in on the ${moduleName} Tree`,
      headerTooltip: getLastChildCheckInTooltip(),
      order: 19,
      tooltipComponent: HeaderCellTooltip,
      tooltipComponentParams: { showColumnName: true },
      hide: true,
      width: 140,
      minWidth: 100,
      headerClass: 'truncate-header-ag-grid',
      filter: 'agDateColumnFilter',
      filterValueGetter: (params) =>
        params?.data?.latestChildCheckInTS ? startOfDay(new Date(params?.data?.latestChildCheckInTS)) : null,
      valueGetter: (params) =>
        params?.data?.latestChildCheckInTS ? convertDateToString4(params?.data?.latestChildCheckInTS) : '',
      cellRenderer: LastCheckInChildrenComponent,
      colId: 'table-last-checkin-children',
      sortable: true,
    },
    {
      colId: 'companies',
      field: 'companies',
      order: 24,
      headerName: 'Companies',
      cellRenderer: Companies,
      hide: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
      suppressColumnsToolPanel: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
      minWidth: 150,
      filter: 'agSetColumnFilter',
      sortable: false,
      filterParams: {
        getValue: (params) => params?.data?.companies?.map((company) => company?.companyName) || [],
      },
    },
    {
      colId: 'empLocations',
      field: 'empLocations',
      order: 24,
      headerName: 'Employee locations',
      cellRenderer: EmpLocations,
      hide: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
      suppressColumnsToolPanel: !checkEnableCompanySupport() || !isCompanyGoalsManagePage,
      minWidth: 200,
      filter: 'agSetColumnFilter',
      sortable: false,
      filterParams: {
        getValue: (params) => params?.data?.empLocations?.map((empLocation) => empLocation) || [],
      },
    },
  ];

  // filtering the columns present in columnsToHide when selectedCohortId is -12 / on myData page
  const modifiedColumnDefs = useMemo(() => {
    const filteredColumns =
      selectedCohortId === -12 ? columnDefs?.filter((column) => !columnsToHide.includes(column.colId)) : columnDefs;
    const checkInColumnsIds = ['table-goal-name', 'table-owner', 'checkinStatus', 'comment', 'table-status'];
    const checkInColumns = filteredColumns.map((column) => {
      if (checkInColumnsIds.includes(column.colId)) {
        return { ...column, hide: false };
      }
      return { ...column, hide: true };
    });
    const sortedColumns = isCheckIn ? checkInColumns.sort((a, b) => a.order - b.order) : filteredColumns;
    return sortedColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckIn, selectedCohortId]);
  const columnDefsResponsive = useMemo(() => [], []);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: moduleName,
      minWidth: isCheckIn ? 340 : 340,
      groupId: 'okrs',
      // pinned: 'left',
      lockPosition: 'left',
      // cellClass: 'locked-col',
      flex: 2,
      filter: 'agMultiColumnFilter',
      colId: 'table-goal-name',
      filterParams: {
        filters: [
          ...(hasImplicitDepartmentFilter
            ? [
                {
                  filter: 'agSetColumnFilter',
                  display: 'accordion',
                  title: 'Filter by Function',
                  filterParams: {
                    suppressMiniFilter: true,
                    keyCreator: (params) => String(params.value.id),
                    getValue: ({ data }) => ({
                      id: data.departmentId,
                      name: data.departmentName,
                    }),
                    valueFormatter: ({ value }) => value.name ?? '(Blanks)',
                    readOnly: true,
                  },
                },
              ]
            : []),
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Level',
            filterParams: {
              values: OBJECTIVE_LEVEL_TAGS.map(({ value }) => value),
              // this is used to display the value in the filter menu
              valueFormatter: ({ value }) => capitalize(startCase(value)),
              // this is used to get the key which is used to filter the data
              getValue: (data) => data.data.objectiveType,
            },
          },
          {
            display: 'accordion',
            title: `Filter by ${moduleName} Title`,
            getValue: (data) => data.data.title,
            filterParams: {
              // copy pasta from ag grid.
              // not touching it for now
              textFormatter: (r) => {
                if (r === null) return null;
                return r
                  .toLowerCase()
                  .replace(/[àáâãäå]/g, 'a')
                  .replace(/æ/g, 'ae')
                  .replace(/ç/g, 'c')
                  .replace(/[èéêë]/g, 'e')
                  .replace(/[ìíîï]/g, 'i')
                  .replace(/ñ/g, 'n')
                  .replace(/[òóôõö]/g, 'o')
                  .replace(/œ/g, 'oe')
                  .replace(/[ùúûü]/g, 'u')
                  .replace(/[ýÿ]/g, 'y');
              },
              filterOptions: ['contains', 'notContains'],
              debounceMs: 200,
              suppressAndOrCondition: true,
            },
          },
          // {
          //   filter: 'agSetColumnFilter'
          // }
        ],
      },
      valueGetter: (data) => data.data ?? {},
      filterValueGetter: (data) => data.data.title ?? '',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
        isReview,
      },
      cellStyle: (params) => ({ overflow: 'visible', paddingLeft: '0px' }),
      sortable: false,
      comparator: (value1, value2) =>
        String(value1).toLocaleLowerCase().localeCompare(String(value2).toLocaleLowerCase()),
      equals: (okr1, okr2) => {
        const isTitleUnchanged = okr1.title === okr2.title;
        const isVisibilityUnchanged = okr1.visibilityType === okr2.visibilityType;
        const ifGoalChildExistsUnchanged = okr1.ifGoalChildExists === okr2.ifGoalChildExists;
        const isProgressTypeUnchanged = okr1.progressType === okr2.progressType;

        return isTitleUnchanged && isVisibilityUnchanged && ifGoalChildExistsUnchanged && isProgressTypeUnchanged;
      },
      cellClass: 'objective-kr-cell-ag-grid-wrap',
      autoHeight: true,
      wrapText: true,
      chartDataType: 'excluded',
    }),
    []
  );

  const autoGroupColumnDefResponsive = {
    headerName: moduleName,
    minWidth: 250,
    flex: 1,
    filter: true,
    colId: 'table-goal-name',
    cellStyle: { height: 'auto', padding: '4px' },
    wrapText: true,
    autoHeight: true,
    menuTabs: [],
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: ObjectiveKRCellResponsive,
    },

    //  hacky way to make sure that the auto group column is updated on the basis of the valueGetter
    valueGetter: (data) =>
      data?.data?.title + data?.data?.progressLevel + data?.data?.progress + data?.data?.avgProgressPercentage,
  };

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  /*
    Row IDs are provided via the getRowId() callback. 
    This results in Row Selection being maintained across 
    Row Data changes (assuming the Row exists in both sets) 
    and the HTML is not redrawn from scratch, 
    resulting in a Row Animation (animateRows=true) of the rows.

    REFER: https://www.ag-grid.com/react-data-grid/data-update-row-data/#example-immutable-store---large-dataset

    NOTE: If you don't do this, on data change, the grid flashes and loses its state. i.e. expanded rows, scroll position, etc.
  */

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  // const onFirstDataRendered = (params) => {
  //   params.api.sizeColumnsToFit();
  // };

  const rowClickHandler = ({ data, column, event }) => {
    if (
      column.colId === 'kebab-menu' ||
      (isCheckIn && (column.colId === 'checkinStatus' || column.colId === 'table-status' || column.colId === 'comment'))
    ) {
      // This is one way to prevent event bubbling.
      // Every column has a column id which is generally the header name.
      // In this case, the last header does not have a name, so the ag grid automatically gave it an ID which is 0(FOR KEBAB MENU).
    } else {
      const { type, uuid, unMasked } = data ?? {};
      if (!unMasked) {
        return;
      }

      sendOpenedKRPanelEvent({
        eventProperties: { source, page: page || source, type: 'Edit KR Panel' },
      });
      dispatch(
        showOKRDetails({
          id: uuid,
          type,
          onDelete: refetchHandler,
          onUpdate: refetchHandler,
          history,
        })
      );
    }
  };

  const getRowStyle = (params) => {
    if (params?.data?.unMasked) {
      return { cursor: 'pointer' };
    }
  };

  const queryClient = useQueryClient();

  const { OBJECTIVE_TYPE, IS_KR_CASCADING_ENABLED } = useOKRConfig();

  const kebabOptions = useCallback(
    (data) => {
      if (isCheckIn) {
        return [];
      }
      const options = generateKebabMenuOptionsRevamp({
        page,
        data,
        dispatch,
        keyResultsName,
        objectivesName,
        queryClient,
        IS_KR_CASCADING_ENABLED,
        OBJECTIVE_TYPE,
        history,
        location,
        source,
        // selectedTimelines
      });

      return options;
    },
    [
      IS_KR_CASCADING_ENABLED,
      OBJECTIVE_TYPE,
      dispatch,
      history,
      isCheckIn,
      keyResultsName,
      location,
      objectivesName,
      page,
      queryClient,
      source,
    ]
  );
  const onColumnVisible = useCallback(() => {
    const curColumnDefs = gridRef?.current?.api?.getColumnDefs();
    if (isCheckIn) {
      const sortedColumns = curColumnDefs.sort((a, b) => (a.order || 0) - (b.order || 0));
      gridRef?.current?.api?.setGridOption('columnDefs', sortedColumns);
    }
  }, [gridRef, isCheckIn]);

  const paginationProps = isCheckIn
    ? {
        paginationPageSize: '10',
      }
    : { paginationAutoPageSize: true };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {
        <AgGridTable
          /* we want to re-initialize this component whenever persistency key changes */
          key={`${gridStatePersistenceKey}+${viewType}`}
          ref={gridRef}
          /* filter persistence props start */
          useFilterModelPersistence
          gridStatePersistenceKey={gridStatePersistenceKey}
          /* filter persistence props end */
          rowData={isCheckIn ? editableRowData : rowData}
          sideBar={isMobileView() ? false : sideBar}
          columnDefs={isMobileView() ? columnDefsResponsive : modifiedColumnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={isMobileView() ? autoGroupColumnDefResponsive : autoGroupColumnDef}
          /* In Mobile view, we have only one column , so pinning it left is creating empty space in center */
          additionalMobileProps={{ pinned: undefined, lockPosition: undefined }}
          // rowClass="ag-row-hide-on-hover"
          treeData
          animateRows
          groupDefaultExpanded={-1}
          getDataPath={getDataPath}
          // domLayout="autoHeight"
          rowHeight={84}
          getRowStyle={getRowStyle}
          onCellClicked={rowClickHandler}
          getRowId={getRowId}
          suppressRowTransform
          loadingOverlayComponent={LoadingOverlayComponent}
          noRowsOverlayComponent={NoRowsOverlayComponent}
          noRowsOverlayComponentParams={{
            moduleName,
            isMyOkr,
            isMyCompany,
            isCheckIn,
          }}
          // debounceVerticalScrollbar={true}
          tooltipShowDelay={500}
          postProcessPopup={postProcessPopup}
          suppressMenuHide // <- this is to force the menu icon to show
          pagination
          {...paginationProps}
          onModelUpdated={() => {
            handleDataModelUpdated();
            onModalUpdated();
          }}
          kebabMenuOptionsGenerator={kebabOptions}
          kebabMenuIsRowSpecific
          isMobileView={isMobileView()}
          onColumnVisible={onColumnVisible}
          // containerAutoHeight={isCheckIn}
          minHeight="250px"
          // these 3 props are used to override the default behavior of the ag grid copy pasting text from the cell and required for inline editing of cells
          ensureDomOrder
          enableCellTextSelection
          processDataFromClipboard={() => null}
          suppressCellFocus
          components={{ CommentComponent }}
          excludeChildrenWhenTreeDataFiltering
        />
      }
    </>
  );
};

export { GoalsObjectiveGenericTreeViewTable };
